import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@edusense/shared-auth-core';
import { getSiteAnalyticsApplications, type AdminApplicationId } from '../apps/adminApplications';
import {
  fetchEmbassyWatchLiveStats,
  fetchSiteVisitsReport,
  type EmbassyWatchLiveStats,
  type SiteVisitsRange,
  type SiteVisitsReport as SiteVisitsReportData,
} from '../types/siteVisits';
import { SiteVisitsReport } from './siteVisits/SiteVisitsReport';

const SITE_APPLICATIONS = getSiteAnalyticsApplications();
const DEFAULT_RANGE: SiteVisitsRange = 30;

export default function SiteVisitsPage() {
  const { token: accessToken } = useAuth();
  // 공개 사이트가 있는 앱만 고른다 — 헤더의 관리 앱(사주 등)과는 별개의 축이다.
  const [applicationId, setApplicationId] = useState<AdminApplicationId>(SITE_APPLICATIONS[0].id);
  const [days, setDays] = useState<SiteVisitsRange>(DEFAULT_RANGE);
  const [report, setReport] = useState<SiteVisitsReportData | null>(null);
  const [liveStats, setLiveStats] = useState<EmbassyWatchLiveStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  const getAuthHeaders = useCallback(() => {
    const headers: Record<string, string> = accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
    const xsrfCookie = document.cookie.split('; ').find((row) => row.startsWith('XSRF-TOKEN='));
    if (xsrfCookie) headers['X-XSRF-TOKEN'] = xsrfCookie.split('=')[1];
    return headers;
  }, [accessToken]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError('');
    if (applicationId === 'embassy-watch') {
      fetchEmbassyWatchLiveStats()
        .then((stats) => {
          if (active) setLiveStats(stats);
        })
        .catch(() => {
          if (active) setLiveStats(null);
        });
    } else {
      setLiveStats(null);
    }
    fetchSiteVisitsReport(applicationId, days, getAuthHeaders())
      .then((next) => {
        if (active) setReport(next);
      })
      .catch((error: unknown) => {
        console.error('Failed to fetch site visits:', error);
        if (active) setLoadError('방문 집계를 불러오지 못했습니다. 인증 상태와 서버 연결을 확인해 주세요.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [applicationId, days, getAuthHeaders, reloadKey]);

  return (
    <SiteVisitsReport
      applications={SITE_APPLICATIONS}
      applicationId={applicationId}
      onSelectApplication={(next) => {
        setReport(null);
        setApplicationId(next);
      }}
      days={days}
      onSelectDays={setDays}
      report={report}
      liveStats={liveStats}
      loading={loading}
      error={loadError}
      onRetry={() => setReloadKey((key) => key + 1)}
    />
  );
}
